<template>
  <div
    :class="['campaign-form-container', { 'mt-0 pt-0 pb-2': props.source && props.source.includes('update'), 'py-2': !props.source }]">
    <h3 class="form-title" v-if="!props.source">Execution Details</h3>
    <b-form class="mt-4">
      <b-form-group label-class="punch-card-form-label d-inline-flex align-items-center pb-0"
        :class="['align-items-center', { 'mb-0': !apiErrors.is_open_duration, 'mb-3': !!apiErrors.is_open_duration }]">
        <template slot="label">
          Campaign Duration
          <switches v-model="campaignData.execution.is_open_duration" theme="custom" color="primary" type-bold="true"
            :emit-on-mount="false" text-enabled="ON" text-disabled="OFF" id="toggle-punch-card-campaign-duration"
            class="ml-2 mb-0" :style="{ top: '-2px' }" @input="evaluteDatePickers('is_open_duration')" />
        </template>
        <b-form-invalid-feedback :force-show="!!apiErrors.is_open_duration">
          {{ apiErrors.is_open_duration }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-row v-if="campaignData.execution.is_open_duration">
        <b-col class="col-12 col-md-6 mb-3 mb-md-0">
          <b-form-group label-class="punch-card-form-label" label="Start Date">
            <datepicker v-model="campaignData.execution.date_start" ref="punchCardCampaignStartDate"
              :config="startDatePickerConfig()" id="input-punch-card-campaign-start-date"
              :class="['bg-transparent', { 'is-invalid': $v.date_start.$errors.length || !!apiErrors.date_start }]"
              @input="evaluteDatePickers('date_start')">
            </datepicker>
            <b-form-invalid-feedback :force-show="$v.date_start.$errors.length > 0 || !!apiErrors.date_start">
              <template v-if="!!apiErrors.date_start">{{ apiErrors.date_start }}</template>
              <p v-else class="mb-0" v-for="error in $v.date_start.$errors" :key="error.$uid">{{ error.$message }}</p>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col class="col-12 col-md-6 mb-3 mb-md-0">
          <b-form-group label-class="punch-card-form-label" label="End Date">
            <datepicker v-model="campaignData.execution.date_end" ref="punchCardCampaignEndDate"
              :config="endDatePickerConfig()" id="input-punch-card-campaign-end-date"
              :class="['bg-transparent', { 'is-invalid': $v.date_end.$errors.length || !!apiErrors.date_end }]"
              @blur="evaluteDatePickers()">
            </datepicker>
            <b-form-invalid-feedback :force-show="$v.date_end.$errors.length > 0 || !!apiErrors.date_end">
              <template v-if="!!apiErrors.date_end">{{ apiErrors.date_end }}</template>
              <p v-else class="mb-0" v-for="error in $v.date_end.$errors" :key="error.$uid">{{ error.$message }}</p>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group label-class="punch-card-form-label" label="Publish Date" class="mb-0"></b-form-group>
      <b-form-group class="mb-0">
        <template slot="label">
          <small>
            <b-form-checkbox id="checkbox-punch-card-campaign-publishing-same-as-start-date"
              v-model="campaignData.execution.publish_date_same_as_start_date" @input="evaluatePublishingDate"
              :disabled="!campaignData.execution.is_open_duration || !campaignData.execution.date_start">
              Same as Start Date
            </b-form-checkbox>
          </small>
        </template>
      </b-form-group>
      <b-form-group :class="{ 'pointer-events-none': campaignData.execution.publish_date_same_as_start_date }">
        <datepicker v-model="campaignData.execution.publishing_date" ref="punchCardCampaignPublishDate"
          :config="publishingDatePickerConfig()" id="input-punch-card-campaign-publish-date"
          :class="['bg-transparent', { 'is-invalid': $v.publishing_date.$errors.length || !!apiErrors.publishing_date }]"
          @input="evaluteDatePickers('publishing_date')">
        </datepicker>
        <b-form-invalid-feedback :force-show="$v.publishing_date.$errors.length > 0 || !!apiErrors.publishing_date">
          <template v-if="!!apiErrors.publishing_date">{{ apiErrors.publishing_date }}</template>
          <p v-else class="mb-0" v-for="error in $v.publishing_date.$errors" :key="error.$uid">{{ error.$message }}</p>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label-class="punch-card-form-label" label="Publish Time">
        <b-input-group :class="['form-control p-0', { 'border-danger': $v.publishing_time.$errors.length }]">
          <vue-timepicker id="input-punch-card-publish-time" placeholder="Pick Time" close-on-complete hide-clear-button
            :hour-range="publishTimeRange.HH" :minute-range="publishTimeRange.mm" class="flex-grow-1"
            @input="evaluatePastPublishTime()" v-model="campaignData.execution.publishing_time"
            :disabled="isPastPublishDate"></vue-timepicker>
          <template slot="append">
            <b-button variant="outline-secondary" disabled class="border-0 shadow-none">
              <i class="bi bi-clock" aria-hidden="true"></i>
            </b-button>
          </template>
        </b-input-group>
        <b-form-invalid-feedback :force-show="$v.publishing_time.$errors.length > 0">
          <p class="mb-0" v-for="error in $v.publishing_time.$errors" :key="error.$uid">{{ error.$message }}</p>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label-class="punch-card-form-label" label="Audience" class="mb-0"></b-form-group>
      <b-form-group>
        <template slot="label">
          <small>
            <b-form-checkbox id="checkbox-punch-card-campaign-accept-guests"
              v-model="campaignData.execution.is_open_to_guest">Accept Guests</b-form-checkbox>
          </small>
        </template>
        <b-dropdown id="dropdown-punch-card-audience" :text="selectedTargetAudienceText" class="w-100"
          :toggle-class="['text-left bg-transparent border text-dark shadow-none w-100', { 'unselected': !campaignData.execution.target_audience, 'border-danger': $v.target_audience.$errors.length || !!apiErrors.target_audience }]"
          menu-class="w-100 p-0" no-caret>
          <b-dropdown-item v-for="(audience, index) in audienceDropdownList"
            :key="`option-punch-card-campaign-target-audience-${index}`"
            :id="`option-punch-card-campaign-target-audience-${index}`"
            @click="campaignData.execution.target_audience = audience.value">
            {{ audience.text }}
          </b-dropdown-item>
        </b-dropdown>
        <b-form-invalid-feedback :force-show="$v.target_audience.$errors.length > 0 || !!apiErrors.target_audience">
          <template v-if="!!apiErrors.target_audience">{{ apiErrors.target_audience }}</template>
          <p v-else class="mb-0" v-for="error in $v.target_audience.$errors" :key="error.$uid">{{ error.$message }}</p>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label-class="punch-card-form-label" label="Frequency" class="mb-0"></b-form-group>
      <b-form-group>
        <template slot="label">
          <small>
            <b-form-checkbox id="checkbox-punch-card-campaign-unlimited-frequency" v-model="isUnliFrequency"
              @input="evaluateFrequency">Unlimited</b-form-checkbox>
          </small>
        </template>
        <b-dropdown id="dropdown-punch-card-frequency" :text="selectedFrequencyText" class="w-100"
          :toggle-class="['text-left bg-transparent border text-dark shadow-none w-100', { 'unselected': !campaignData.execution.frequency, 'border-danger': $v.frequency.$errors.length || !!apiErrors.frequency }]"
          menu-class="w-100 p-0" :disabled="campaignData.execution.frequency === 0" no-caret>
          <b-dropdown-item v-for="(frequency, index) in fequencyDropdownList"
            :key="`option-punch-card-campaign-frequency-${index}`" :id="`option-punch-card-campaign-frequency-${index}`"
            @click="campaignData.execution.frequency = frequency">
            {{ frequency }}
          </b-dropdown-item>
        </b-dropdown>
        <b-form-invalid-feedback :force-show="$v.frequency.$errors.length > 0 || !!apiErrors.frequency">
          <template v-if="!!apiErrors.frequency">{{ apiErrors.frequency }}</template>
          <p v-else class="mb-0" v-for="error in $v.frequency.$errors" :key="error.$uid">{{ error.$message }}</p>
        </b-form-invalid-feedback>
      </b-form-group>
    </b-form>
  </div>
</template>

<script>
import { computed, defineComponent, getCurrentInstance, onMounted, onBeforeUnmount, onUpdated, ref } from "@vue/composition-api";
import { mapFields } from "vuex-map-fields";
import { mapActions, mapGetters, mapState } from "vuex";
import Switches from "vue-switches";
import moment from "moment";
import Datepicker from "vue-bulma-datepicker";
import useVuelidate from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";
import { fieldRequiredErrorMessage } from "../punch.card.utilities";
import { DateTime } from "luxon";
import VueTimepicker from "vue2-timepicker";

export default defineComponent({
  props: ["source"],
  components: { Switches, Datepicker, VueTimepicker },
  computed: {
    ...mapFields("punchCard", ["campaignData", "errors"]),
    ...mapGetters({
      targetSegmentOptions: "engagementBroadcasting/targetSegmentOptions",
      campaignDetails: "punchCard/campaignDetails"
    }),
    ...mapState("punchCard", ["campaignDataUntouched"])
  },
  methods: {
    ...mapActions({
      getTargetSegmentOptions: "engagementBroadcasting/getTargetSegmentOptions"
    })
  },
  setup(props, { root }) {
    const currentInstance = getCurrentInstance();

    const form = computed(() => currentInstance.proxy.campaignData.execution || {});

    const startDatePickerConfig = () => {
      const { date_start } = computed(() => currentInstance.proxy.campaignData.execution || {}).value;
      const minDate = () => DateTime.fromISO(date_start) < DateTime.now() ? date_start : moment().format("YYYY-MM-DD");

      return {
        altInput: true,
        altFormat: "m/d/Y",
        dateFormat: "Y-m-d",
        static: true,
        mode: "single",
        minDate: minDate(),
        onChange: () => $v.value.date_start.$validate(),
        disable: [
          function (date) {
            if (date_start) {
              const luxonStartDate = DateTime.fromJSDate(date);
              return luxonStartDate.toISO() !== DateTime.fromISO(date_start).toISO() &&
                luxonStartDate < DateTime.now().minus({ days: 1 });
            } else return false;
          }
        ]
      }
    };

    const endDatePickerConfig = () => {
      const { date_start } = form.value;
      const dateTomorrow = moment().add(1, "d").format("YYYY-MM-DD");

      return {
        altInput: true,
        altFormat: "m/d/Y",
        dateFormat: "Y-m-d",
        minDate: date_start
          ? moment(date_start).add(1, "d").format("YYYY-MM-DD")
          : dateTomorrow
      };
    };

    const publishingDatePickerConfig = () => {
      const { publishing_date } = computed(() => currentInstance.proxy.campaignData.execution || {}).value;
      const minDate = () => DateTime.fromISO(publishing_date) < DateTime.now() ? publishing_date : moment().format("YYYY-MM-DD");

      return {
        altInput: true,
        altFormat: "m/d/Y",
        dateFormat: "Y-m-d",
        static: true,
        mode: "single",
        minDate: minDate(),
        disable: [
          function (date) {
            if (publishing_date) {
              const luxonPublishDate = DateTime.fromJSDate(date);
              return luxonPublishDate.toISO() !== DateTime.fromISO(publishing_date).toISO() &&
                luxonPublishDate < DateTime.now().minus({ days: 1 });
            } else return false;
          }
        ]
      }
    };

    const evaluatePublishingDate = (value) => {
      if (value && form.value.date_start) currentInstance.proxy.campaignData.execution.publishing_date = form.value.date_start;

      else {
        evaluateIsOpenDuration();
      }
    };

    const audienceDropdownList = computed(() => (currentInstance.proxy.targetSegmentOptions || []));

    const selectedTargetAudienceText = computed(() => {
      const selected = currentInstance.proxy.campaignData.execution.target_audience;

      if (selected) {
        const selectedAudience = audienceDropdownList.value.find(({ value }) => value === selected);
        if (selectedAudience) return selectedAudience.text;
      }

      return "Select Audience";
    });

    const isUnliFrequency = ref(false);

    const fequencyDropdownList = Array.from({ length: 10 }, (_, i) => i + 1);

    const selectedFrequencyText = computed(() => {
      if (currentInstance.proxy.campaignData.execution.frequency > 0) return currentInstance.proxy.campaignData.execution.frequency.toString();

      return "Select Frequency";
    });

    const evaluateFrequency = (value) => {
      currentInstance.proxy.campaignData.execution.frequency = value ? 0 : null;
    }

    onMounted(async () => {
      await currentInstance.proxy.getTargetSegmentOptions();

      root.$on("validateForm", () => {
        evaluatePastPublishTime();

        $v.value.$touch();

        if ($v.value.$dirty && !$v.value.$invalid) {
          root.$emit("proceedToNextStep", currentInstance.proxy.$route.name === "PunchCardCreate" ? "create" : props.source);
        }

        evaluteDatePickers();
      });
    });

    onUpdated(() => {
      isUnliFrequency.value = currentInstance.proxy.campaignData.execution.frequency === 0;

      const datePickers = document.querySelectorAll("input[type='hidden']");

      datePickers.forEach((datePicker) => {
        if (datePicker.classList.value.includes('is-invalid')) datePicker.nextElementSibling.classList.add('is-invalid', 'border-danger');
        else datePicker.nextElementSibling.classList.remove('is-invalid', 'border-danger');
      });
    });

    onBeforeUnmount(() => {
      root.$off("validateForm");
      currentInstance.proxy.errors = null;
    });

    const rules = computed(() => ({
      date_start: {
        required: helpers.withMessage(fieldRequiredErrorMessage, requiredIf(() => form.value.is_open_duration)),
        mustBeOnOrAfterPublishingDate: helpers.withMessage("Start date must be on or after the publish date.", () => isDateStartPastPublishDate())
      },
      date_end: {
        required: helpers.withMessage(fieldRequiredErrorMessage, requiredIf(() => form.value.is_open_duration))
      },
      publishing_date: {
        required: helpers.withMessage(fieldRequiredErrorMessage, required)
      },
      publishing_time: {
        required: helpers.withMessage(fieldRequiredErrorMessage, () => form.value.publishing_time.HH && form.value.publishing_time.mm),
        mustNotBePastTime: helpers.withMessage("Start time should be greater than or equal to the current time.", () =>
          !helpers.req(isPublishDateToday.value && form.value.publishing_time.HH && form.value.publishing_time.mm) || !isPastPublishTime.value)
      },
      target_audience: {
        required: helpers.withMessage(fieldRequiredErrorMessage, required)
      },
      frequency: {
        required: helpers.withMessage(fieldRequiredErrorMessage, requiredIf(() => !isUnliFrequency.value))
      }
    }));

    const $v = useVuelidate(rules, form);

    const evaluateIsOpenDuration = () => {
      if (!currentInstance.proxy.errors) currentInstance.proxy.errors = {};

      if (
        (form.value.publish_date_same_as_start_date &&
          !form.value.is_open_duration) ||
        (currentInstance.proxy.campaignDetails &&
          form.value.publish_date_same_as_start_date &&
          !form.value.is_open_duration &&
          form.value.is_open_duration !== Boolean(!currentInstance.proxy.campaignDetails.is_open_duration))
      ) {
        currentInstance.proxy.errors["is_open_duration"] = "Cannot toggle off because Same as Start Date is enabled.";
        currentInstance.proxy.campaignData.execution.is_open_duration = true;
      } else {
        delete currentInstance.proxy.errors.is_open_duration;
      }
    };

    const evaluteDatePickers = (field) => {
      const { date_start, publish_date_same_as_start_date, date_end } = computed(() => currentInstance.proxy.campaignData.execution || {}).value;

      if (field === "is_open_duration") {
        evaluateIsOpenDuration();
      } else if (
        field === "date_start" &&
        publish_date_same_as_start_date
      ) {
        currentInstance.proxy.campaignData.execution.publishing_date = date_start;
      } else if (
        field === "date_start" &&
        DateTime.fromISO(date_end) < DateTime.fromISO(date_start)
      ) {
        currentInstance.proxy.campaignData.execution.date_end = null;
      } else if (field === "publishing_date") {
        if (form.value.is_open_duration && form.value.date_start)
          $v.value.date_start.$validate();
        if (form.value.publishing_time && form.value.publishing_time.HH && form.value.publishing_time.mm)
          evaluatePastPublishTime();
      }
    };

    const apiErrors = computed(() => root.$store.state.punchCard.errors || {});

    const isPublishDateToday = computed(() => DateTime.now().toISODate() === DateTime.fromISO(form.value.publishing_date).toISODate() || false);

    const publishTimeRange = computed(() => {
      const timeRange = {
        HH: [],
        mm: []
      };
      if (isPublishDateToday.value) {
        const { hour, minute } = computed(() => DateTime.now() || {}).value;
        const { publishing_time } = computed(() => currentInstance.proxy.campaignData.execution || {}).value;

        timeRange.HH = Array.from({ length: 24 - hour }, (_, HH) => HH + hour);

        if (hour === Number(publishing_time.HH))
          timeRange.mm = Array.from({ length: 60 - minute }, (_, mm) => mm + minute);
        else
          timeRange.mm = Array.from({ length: 60 }, (_, mm) => mm);
      } else {
        timeRange.HH = Array.from({ length: 24 }, (_, HH) => HH);
        timeRange.mm = Array.from({ length: 60 }, (_, mm) => mm);
      }
      return timeRange;
    });

    const isPastPublishTime = ref(true);
    const isPastPublishDate = computed(() => DateTime.fromISO(form.value.publishing_date) < DateTime.now().minus({ days: 1 }) || false);
    const isDateStartPastPublishDate = () => {
      const { is_open_duration, date_start, publishing_date } = computed(() => form.value || {}).value;
      if (is_open_duration && date_start && publishing_date)
        return DateTime.fromISO(publishing_date).minus({ days: 1 }) < DateTime.fromISO(date_start);
      else
        return true;
    };

    const evaluatePastPublishTime = () => {
      
      const standardRules = () => DateTime.fromSQL(`${DateTime.now().toISODate()} ${form.value.publishing_time.HH}:${form.value.publishing_time.mm}`) < DateTime.now().minus({ minutes: 1 });

      if (!currentInstance.proxy.source &&
        isPublishDateToday.value &&
        form.value.publishing_time.HH &&
        form.value.publishing_time.mm
      )
        isPastPublishTime.value = standardRules();
      else if (
        currentInstance.proxy.source &&
        currentInstance.proxy.source.includes("update") &&
        form.value.publishing_time.HH &&
        form.value.publishing_time.mm &&
        currentInstance.proxy.campaignDataUntouched
      ) {
        const { publishing_date, publishing_time } = computed(() => currentInstance.proxy.campaignDataUntouched || {}).value;
        isPastPublishTime.value = isPublishDateToday.value && (publishing_date !== form.value.publishing_date ||
          JSON.stringify(publishing_time) !== JSON.stringify(form.value.publishing_time)) && standardRules();
      } else isPastPublishTime.value = false;

      $v.value.publishing_time.$validate();
    };

    return {
      startDatePickerConfig,
      endDatePickerConfig,
      publishingDatePickerConfig,
      evaluatePublishingDate,
      selectedTargetAudienceText,
      audienceDropdownList,
      fequencyDropdownList,
      selectedFrequencyText,
      isUnliFrequency,
      evaluateFrequency,
      $v,
      evaluteDatePickers,
      props,
      apiErrors,
      publishTimeRange,
      evaluatePastPublishTime,
      isPastPublishDate
    }
  }
});

</script>

<style lang="scss" scoped>
@import "~vue2-timepicker/dist/VueTimepicker.css";
</style>

<style lang="scss">
#dropdown-punch-card-audience,
#dropdown-punch-card-frequency {
  .dropdown-toggle {
    height: 44px;
    background: transparent url("~@/assets/images/caret-down-2.svg") no-repeat right 12px center;
    padding-right: 2.75rem;

    &.unselected {
      color: #87929D !important;
    }
  }

  .dropdown-menu {
    max-height: 300px;
    background: #fff;
    overflow-y: auto;
  }
}

.pointer-events-none {
  pointer-events: none;
}

#input-punch-card-publish-time {
  border: 0 !important;
  font-family: MainFontRegular, sans-serif;

  &:disabled {
    background-color: transparent;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #C2C8CC;
    font-size: 16px;
  }

  &~.dropdown>.select-list .active {
    background: #FF6B00;
  }
}
</style>
